import { ApiFile } from "@/types/common.types.ts";
import {
  ApiImportDataType,
  ApiImportFieldUpdateBehavior,
  ApiImportGlobalUpdateBehavior,
  ApiImportNewValueBehavior,
  ApiImportPayloadColumn,
  ApiImportPayloadCustomTableKey,
  ApiImportPayloadSave,
} from "@/types/import-data.types.ts";
import { createContext, useContext } from "react";

export enum ImportDataEditStep {
  preset = "preset",
  preview = "preview",
  common_settings = "common_settings",
  match = "match",
  confirmation = "confirmation",
  final = "final",
}

export interface ColumnFormData extends Omit<ApiImportPayloadColumn, "create_new_values"> {
  create_new_values: ApiImportNewValueBehavior | null;
  enabled: boolean;
}

export interface MatchFormData {
  columns: ColumnFormData[];
  custom_tables_keys: ApiImportPayloadCustomTableKey[];
}

export interface ImportDataEditContextType {
  step: ImportDataEditStep;
  file: ApiFile | null;
  setFile: (file: ApiFile | null) => void;
  importId: number | undefined;
  setImportId: (importId: number | undefined) => void;
  importPayload: ApiImportPayloadSave | null;
  setImportPayload: (payload: ApiImportPayloadSave | null) => void;
  rowsCount: number;
  setRowsCount: (count: number) => void;
  importType: ApiImportDataType;
  setImportType: (type: ApiImportDataType) => void;
  importBehavior: ApiImportGlobalUpdateBehavior;
  setImportBehavior: (behavior: ApiImportGlobalUpdateBehavior) => void;
  matchingBy: number | null;
  setMatchingBy: (matchingBy: number | null) => void;
  selectedCustomTableId: number | null;
  setSelectedCustomTableId: (id: number | null) => void;
  selectedCustomTableName: string | null;
  setSelectedCustomTableName: (name: string | null) => void;
  defaultUpdateBehavior: ApiImportFieldUpdateBehavior;
  setDefaultUpdateBehavior: (behavior: ApiImportFieldUpdateBehavior) => void;
  nextButtonText: string;
  goToNextStep: () => void;
  onBack: () => void;
  registerOnNext: (handler: (() => void) | null) => void;
  isNextLoading: boolean;
  setNextLoading: (loading: boolean) => void;
}

export const ImportDataEditContext = createContext<ImportDataEditContextType | null>(null);

export const useImportDataEditContext = () => {
  const context = useContext(ImportDataEditContext);
  if (!context) {
    throw new Error("useImportDataEditContext must be used within ImportDataEditProvider");
  }
  return context;
};
